import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Swal from "sweetalert2";
import logo from "../../../../assets/Discount_App_logo.png";
import loginImage from "../../../../assets/business/images/login-page-img.png";
import { handleServerValidations, setLocalKey } from "../../../../utils/commonfunction";
import { SWAL_SETTINGS, ROLE } from "../../../../utils/Constants";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../utils/customError";
import { login, resendRegisterOtp } from "../../services/auth.services";
import * as Yup from "yup";

const BusinessLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .required(t("Email is required"))
      .test("is-valid-email-or-phone", t("Enter a valid email"), function (value) {
        if (!value) return false;

        const emailRegex = /^(?!.*\.\.)[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const phoneRegex = /^\+?[0-9]{1,4}[\s]?[0-9]{6,14}$/;

        return emailRegex.test(value) || phoneRegex.test(value);
      }),
    password: Yup.string().trim().min(6, "Minimum 6 characters").required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validateOnBlur: false,
    validationSchema: validationSchema,
    onSubmit: (values, {}) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^\+(\d{1,4})\s(\d{6,15})$/;
      const formData = new FormData();
      const input = values.email.trim();

      if (emailRegex.test(input)) {
        formData.append("email", input.toLowerCase());
      } else if (phoneRegex.test(input)) {
        const match = input.match(phoneRegex);
        const countryCode = `+${match[1]}`;
        const phone = match[2];
        const fullNumber = `${countryCode} ${phone}`;
        const phone_number = {
          countryCode,
          phone,
          fullNumber,
        };
        formData.append("phone_number", JSON.stringify(phone_number));
      } else {
        Swal.fire({
          icon: "error",
          text: "Please enter a valid phone number (e.g., +353 851234567)",
          ...SWAL_SETTINGS,
        });
        return;
      }
      formData.append("password", values.password);
      login(formData)
        .then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            setLocalKey(ROLE.BUSINESS, JSON.stringify(response.data));
            const isComplete = response?.data?.isCompleteProfile === true || response?.data?.isCompleteProfile === "true";
            console.log(isComplete);
            if (isComplete) {
              navigate("/business/dashboard");
            } else {
              navigate("/business/complete-profile/business-description");
            }
          } else if (response?.error?.requiresVerification && response.error.email) {
            const verifyEmail = response.error.email;
            const otpData = new FormData();
            otpData.append("email", verifyEmail);
            resendRegisterOtp(otpData).finally(() => {
              Swal.fire({
                icon: "info",
                text: "Your email is not verified yet. We've sent a new verification code to your email.",
                ...SWAL_SETTINGS,
              });
              navigate("/business/register-otp-verification", { state: { email: verifyEmail } });
            });
          } else {
            const message = handleServerValidations(response);
            Swal.fire({
              icon: "error",
              html: message,
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          console.log("error===>", error);
        });
    },
  });

  const handleNavigate = () => {
    navigate("/business/register");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <section className="welcome-screen">
        <div className="row m-0 align-items-center vh-100">
          <div className="col-md-6 p-0">
            <div className="welcome-screen-box login-box">
              <div className="welcome-screen-box-in py-4">
                <div className="back-button">
                  <Link to="/business" className="d-flex align-items-center gap-2">
                    <i className="fi-rr-arrow-small-left"></i> <span>Back</span>
                  </Link>
                </div>
                <div className="logo mb-4 text-center">
                  <a href="#" to="/business/login">
                    <img src={logo} alt="logo-img" />
                  </a>
                </div>
                <div className="heading-text-auth mb-3">
                  <h3 className="h3-headline text-center">Log In</h3>
                  <p className="subtitle-1 text-center mt-3">Let's Get Started!</p>
                </div>
                <form onSubmit={formik.handleSubmit} className="text-start gap-3 w-100 mt-3">
                  <div className="text-start gap-3">
                    <div className="input mb-3">
                      <label htmlFor="email" className="form-label subtitle-1">
                        Email<span className="requirestar">*</span>
                      </label>
                      <input
                        id="email"
                        type="text"
                        className="form-control"
                        name="email"
                        placeholder="Enter email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          formik.setFieldValue("email", e.target.value.trim());
                          formik.handleBlur(e);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === " ") e.preventDefault();
                        }}
                      />

                      <span className="text-danger">
                        <CustomError name="email" form={formik} />
                      </span>
                    </div>
                    <div className="input mb-3">
                      <label className="form-label subtitle-2">
                        Password<span className="requirestar">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          name="password"
                          placeholder="Enter your password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          onBlur={(e) => {
                            formik.setFieldValue("password", e.target.value.trim());
                            formik.handleBlur(e);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === " ") e.preventDefault();
                          }}
                        />

                        <button
                          type="button"
                          className="btn btn-link position-absolute top-50 end-0 translate-middle-y pe-3"
                          onClick={togglePasswordVisibility}
                          style={{
                            border: "none",
                            background: "none",
                            padding: "0",
                            color: "#6c757d",
                            textDecoration: "none",
                          }}>
                          <i className={showPassword ? "fi-rr-eye-crossed" : "fi-rr-eye"}></i>
                        </button>
                      </div>
                      <span className="text-danger">
                        <CustomError name="password" form={formik} />
                      </span>
                    </div>
                  </div>

                  <a
                    href="/business/forget-password"
                    className="forgot-pass subtitle-2"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/business/forget-password");
                    }}>
                    Forgot Password?
                  </a>
                  <div className="get-started-btn w-50 d-flex flex-column gap-3">
                    <button className="btn btn-oring" type="submit">
                      Login
                    </button>
                  </div>
                  <div className="body-2 form-last-text mt-4 text-center" htmlFor="agreeCheck">
                    Didn't have an account?
                    <a
                      className="subtitle-2"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigate();
                      }}>
                      Create one
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="col-md-6 p-0">
            <div className="hero-img">
              <img src={loginImage} className="" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BusinessLogin;
